import React, { useEffect, useState } from 'react';
import { Routes, Route, useParams, useLocation, Navigate, Outlet } from 'react-router-dom';
import Homepage from './components/Homepage';
import SeriesPage from './components/SeriesPage';
import NotFoundPage from './components/NotFoundPage';
import StreamingDashboard from './components/StreamingDashboard';
import AuthWrapper from './components/auth/AuthWrapper';
import analytics from './services/analytics';
import LandingRedirectHandler from './components/auth/LandingRedirectHandler';
import ProtectedRoute from './components/auth/ProtectedRoute';
import MyListsPage from './pages/MyListsPage';
import { AppLoadProvider, useAppLoad } from './contexts/AppLoadContext';
import MobileIncompatiblePage from './components/MobileIncompatiblePage';
import SmartAppBanner from './components/SmartAppBanner';
import DeviceDetection from './utils/DeviceDetection';
import { resolvePageTitle } from './utils/SEOUtils';

import SearchPage from './components/search/SearchPage';
import ContentRouter from './components/ContentRouter';
import AppleAppSiteAssociation from './components/AppleAppSiteAssociation';
import AndroidAssetLinks from './components/AndroidAssetLinks';
import AdsTxt from './components/AdsTxt';
import EmailConsentPage from './components/EmailConsentPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PaywallPage from './pages/PaywallPage';
import FightersPage from './components/FightersPage';
import FighterDetailPage from './components/FighterDetailPage';
import EventsPage from './components/events/EventsPage';
import EventDetailPage from './components/events/EventDetailPage';
import RailPage from './components/RailPage';
import AppLayout from './components/layout/AppLayout';
import SubscriptionGate from './components/auth/SubscriptionGate';

// DSP-era PDP and category URLs: a movie is its watch page.
const MovieRedirect = () => {
  const { movieId } = useParams();
  return <Navigate to={`/watch/${movieId}`} replace />;
};

const slugify = text =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

// A DSP category is the home rail whose title slugifies to it (rails carry no slug of their own,
// and /v1/home is already loaded); with no such rail it goes home.
const CategoryRedirect = () => {
  const { categorySlug } = useParams();
  const { categories, loading } = useAppLoad();
  if (loading) {
    return null;
  }
  const rail = categories.find(c => c.v1 && slugify(c.name) === slugify(categorySlug));
  return <Navigate to={rail ? `/rail/${rail._id}` : '/'} replace />;
};

const WatchPageWrapper = () => {
  return <ContentRouter />;
};

const SearchPageWrapper = () => {
  return (
    <React.Suspense fallback={<div>Loading...</div>}>
      <SearchPage />
    </React.Suspense>
  );
};

const MyStreamsWrapper = () => {
  return (
    <div className="min-h-screen p-6 text-white bg-gray-900">
      <div className="mx-auto max-w-7xl">
        <h1 className="mb-6 text-3xl font-bold">My Streams</h1>
        <p className="text-gray-300">Your personal streaming content will appear here.</p>
      </div>
    </div>
  );
};

const CreateStreamWrapper = () => {
  return (
    <div className="min-h-screen p-6 text-white bg-gray-900">
      <div className="mx-auto max-w-7xl">
        <h1 className="mb-6 text-3xl font-bold">Create Stream</h1>
        <p className="text-gray-300">Stream creation interface coming soon...</p>
      </div>
    </div>
  );
};

const EXTERNAL_LINKS = {
  privacy: 'https://www.bkfc.com/privacy-policy',
  terms: 'https://www.bkfc.com/terms-of-use',
  about: 'https://www.bkfc.com/about',
  contact: 'https://www.bkfc.com/contact',
};

const LEGACY_PAGE_REDIRECTS = {
  '/privacy-policy': EXTERNAL_LINKS.privacy,
  '/terms-of-use': EXTERNAL_LINKS.terms,
  '/about': EXTERNAL_LINKS.about,
  '/contact': EXTERNAL_LINKS.contact,
  '/ccpa': EXTERNAL_LINKS.privacy,
  '/do-not-sell': EXTERNAL_LINKS.privacy,
};

function App() {
  // Get current location for deep linking
  const location = useLocation();

  // SPA page view: GA4's auto pageview only fires on real loads, so send one
  // on every route change (fix for client-side routing). Redaction of any
  // sensitive query params happens inside the analytics layer.
  // Watch routes are the exception: the pixel gets a richer `video_view` (with
  // content context, fired from ContentRouter once the fight loads) instead of
  // a bare page_view — but GA4 still needs its page_view for SPA reporting.
  useEffect(() => {
    const isWatch = location.pathname.startsWith('/watch/');
    // Give every route a correct, on-brand title up front (many pages set none,
    // so document.title would otherwise be stale/leftover). Content pages
    // upgrade this to the specific fight/fighter name via their own meta tags;
    // reading document.title at fire time picks up that upgrade when it lands.
    document.title = resolvePageTitle(location.pathname);
    // Debounced so a view that immediately redirects (e.g. `/` → `/login`,
    // gated routes) doesn't emit a throwaway page_view: if the route changes
    // again within the window, the cleanup cancels the pending hit.
    const t = setTimeout(() => {
      analytics.trackPageView(window.location.href, document.title, { pixel: !isWatch });
    }, 400);
    return () => clearTimeout(t);
  }, [location.pathname, location.search]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Device detection state
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [deviceCheckComplete, setDeviceCheckComplete] = useState(false);
  const [showSmartBanner, setShowSmartBanner] = useState(false);

  // Check device type on component mount and route changes
  useEffect(() => {
    const checkDevice = () => {
      const currentPath = location.pathname;
      const externalRedirectUrl = LEGACY_PAGE_REDIRECTS[currentPath];

      if (externalRedirectUrl) {
        window.location.replace(externalRedirectUrl);
        return;
      }

      const shouldRedirect = DeviceDetection.shouldRedirectToApp();

      // Define allowed paths for mobile users
      const allowedMobilePaths = [
        '/login',
        '/signup',
        '/create-account',
        '/forgot-password',
        '/reset-password',
        '/subscribe',
        '/email-consent', // Email consent page for post-registration
      ];

      // Define content paths that should show mobile detection page (not redirect)
      const contentPaths = [
        '/watch', // Watch pages
        '/movie', // Movie PDP
        '/series', // Series PDP
      ];

      // Check if current path is allowed for mobile users
      const isMobileAllowedPath = allowedMobilePaths.includes(currentPath);

      // Check if current path is a content path (should show detection page)
      const isContentPath = contentPaths.some(path => currentPath.startsWith(path));

      // Determine mobile behavior:
      // - Allowed paths: normal access
      // - Content paths: show mobile detection page
      // - Other paths (homepage, etc.): redirect to the main app entry
      const shouldRedirectMobile = shouldRedirect && !isMobileAllowedPath;
      const shouldShowDetectionPage = shouldRedirectMobile && isContentPath;
      const shouldRedirectToLanding = shouldRedirectMobile && !isContentPath;

      // Check if device should show smart banner (mobile/tablet but not redirect)
      const isMobile = DeviceDetection.isMobile();
      const isIPad = DeviceDetection.isIPad();
      const isAndroidTablet =
        /Android/i.test(navigator.userAgent) && !/Mobile/i.test(navigator.userAgent);
      const shouldShowBanner = (isMobile || isIPad || isAndroidTablet) && !shouldRedirectMobile;

      setIsMobileDevice(shouldShowDetectionPage);
      setShowSmartBanner(shouldShowBanner);
      setDeviceCheckComplete(true);

      // Handle redirect to app entry for non-content pages
      // Use sessionStorage to prevent redirect loops
      // BUT: Don't redirect if there's a channel parameter (deep link)
      if (shouldRedirectToLanding) {
        const hasChannelParam = location.search.includes('channel=');
        const hasRedirected = sessionStorage.getItem('mobileRedirectedToLanding');

        if (!hasChannelParam && !hasRedirected) {
          sessionStorage.setItem('mobileRedirectedToLanding', 'true');
          window.location.href = '/';
        } else if (hasChannelParam) {
          // If there's a channel parameter, show the mobile detection page instead
          // This allows the deep link to work through the mobile intercept
          setIsMobileDevice(true);
        } else {
          // If already redirected once, show detection page instead of looping
          setIsMobileDevice(true);
        }
      }
    };

    // Run check immediately
    checkDevice();

    // Also run check on window resize (orientation change)
    const handleResize = () => {
      setTimeout(checkDevice, 100); // Small delay to ensure accurate measurements
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [location.pathname]); // Add location.pathname as dependency

  // Show loading while device check is in progress
  if (!deviceCheckComplete) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#222222',
          color: 'white',
          fontFamily: 'Heebo, sans-serif',
        }}
      >
        <div>Loading BKFC+...</div>
      </div>
    );
  }

  // Show mobile detection page only for content pages
  if (isMobileDevice) {
    return <MobileIncompatiblePage />;
  }

  // Normal app for desktop/tablet users with smart banner
  return (
    <div className="App">
      {/* Smart App Banner for mobile/tablet users */}
      {showSmartBanner && <SmartAppBanner currentPath={location.pathname} />}

      <AuthWrapper>
        <AppLoadProvider>
          <LandingRedirectHandler>
            <Routes>
              {/* Legacy landing URL now routes to app entrypoint */}
              <Route path="/landing" element={<Navigate to="/" replace />} />

              {/* Auth routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/create-account" element={<SignupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              {/* Well-known files for app deep linking */}
              <Route
                path="/.well-known/apple-app-site-association"
                element={<AppleAppSiteAssociation />}
              />
              <Route path="/.well-known/assetlinks.json" element={<AndroidAssetLinks />} />

              {/* Ads.txt for programmatic advertising */}
              <Route path="/ads.txt" element={<AdsTxt />} />

              {/* Subscription paywall - requires auth but NOT subscription */}
              <Route
                path="/subscribe"
                element={
                  <ProtectedRoute>
                    <SubscriptionGate requireSubscription={false} redirectIfSubscribedTo="/">
                      <PaywallPage />
                    </SubscriptionGate>
                  </ProtectedRoute>
                }
              />

              {/* Content browses signed out; the gate is at play. */}
              <Route element={<AppLayout />}>
                <Route path="/" element={<Homepage />} />
                <Route path="/fighters" element={<FightersPage />} />
                <Route path="/fighters/:id" element={<FighterDetailPage />} />
                <Route path="/events" element={<EventsPage />} />
                <Route path="/events/upcoming" element={<EventsPage />} />
                <Route path="/events/past" element={<EventsPage />} />
                <Route path="/events/:id" element={<EventDetailPage />} />
                <Route path="/watch/:channelSlug" element={<WatchPageWrapper />} />
                <Route path="/watch/:channelSlug/:episodeId" element={<WatchPageWrapper />} />
                <Route path="/movie/:movieId" element={<MovieRedirect />} />
                <Route path="/series/:seriesId" element={<SeriesPage />} />
                <Route path="/category/:categorySlug" element={<CategoryRedirect />} />
                <Route path="/rail/:id" element={<RailPage />} />
                <Route path="/search" element={<SearchPageWrapper />} />

                {/* Account pages need a signed-in user (not a subscription). */}
                <Route
                  element={
                    <ProtectedRoute>
                      <Outlet />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/my-lists" element={<MyListsPage />} />
                  <Route path="/my-lists/:tab" element={<MyListsPage />} />
                  <Route path="/settings" element={<StreamingDashboard />} />
                  <Route path="/my-streams" element={<MyStreamsWrapper />} />
                  <Route path="/create-stream" element={<CreateStreamWrapper />} />
                  <Route path="/dashboard" element={<Navigate to="/settings" replace />} />
                  {/* Email Consent Page for Post-Registration */}
                  <Route path="/email-consent" element={<EmailConsentPage />} />
                </Route>

                {/* 404 Catch-all route - must be last */}
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </LandingRedirectHandler>
        </AppLoadProvider>
      </AuthWrapper>
    </div>
  );
}

export default App;
